import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { useScroll, useSpring, useTransform } from 'framer-motion'

import { useSectionNav } from 'hooks/useSectionNav'
import { useMedia } from 'hooks/useMedia'
import {
  Content,
  NextButton,
  PrevButton,
  ScrollContainer,
  multiColumnBreakpoint,
} from './styled'

type Props = {
  children?: ReactNode
  menuTitle?: string
  isIntro?: boolean
  isFollowedByLongread?: boolean
  hasNextButton?: boolean
}

export const LongRead: FC<Props> = ({
  children,
  menuTitle,
  isIntro,
  isFollowedByLongread,
  hasNextButton = true,
}) => {
  const { thisSection, handlePrevClick, handleNextClick } = useSectionNav(
    menuTitle,
    'start'
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  })
  const transform = useTransform(scrollYProgress, [0.05, 0.9], [0, 1])
  const physics = { damping: 5, mass: 0.1, stiffness: 40 }
  const translateX = useSpring(transform, physics)
  const isLarge = useMedia([multiColumnBreakpoint], [true], false)
  const [containerHeight, setContainerHeight] = useState(300)

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      updateHeight()
    })

    const updateHeight = () => {
      if (contentRef.current) {
        if (isLarge) {
          setContainerHeight(
            Math.round(
              (contentRef.current.scrollWidth /
                contentRef.current.clientWidth) *
                100
            )
          )
        } else {
          setContainerHeight(
            Math.round(
              (contentRef.current.scrollHeight /
                contentRef.current.clientHeight) *
                100
            )
          )
        }
      }
    }

    updateHeight()

    if (contentRef.current) {
      observer.observe(contentRef.current)
    }

    translateX.on('change', value => {
      if (contentRef.current) {
        if (isLarge) {
          contentRef.current.scrollLeft =
            (contentRef.current?.scrollWidth -
              contentRef.current?.clientWidth) *
            value
        } else {
          contentRef.current.scrollTop =
            (contentRef.current?.scrollHeight -
              contentRef.current?.clientHeight) *
            value
        }
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [isLarge])

  return (
    <ScrollContainer
      ref={containerRef}
      $isIntro={isIntro}
      style={{ height: `${containerHeight}vh` }}
      data-alignment="start"
      {...(menuTitle ? { 'data-menu-title': menuTitle } : null)}
    >
      <PrevButton
        isCurrent={thisSection?.isActive ?? false}
        onClick={handlePrevClick}
      />
      <Content>
        <div ref={contentRef}>{children}</div>
      </Content>
      {hasNextButton && (
        <NextButton
          isCurrent={thisSection?.isActive ?? false}
          onClick={handleNextClick}
          isFollowedByLongread={isFollowedByLongread}
          isNext
        />
      )}
    </ScrollContainer>
  )
}
